/**
 * FooterSection Component
 *
 * Displays site footer with contact info, quick links, and legal info.
 * Final section on the page - provides navigation and contact.
 *
 * Spec Reference: spec.md - Section 5.9 "Footer Section"
 */

import React from 'react'
import {
  Box,
  Container,
  Typography,
  Grid,
  Link,
  IconButton,
} from '@mui/material'
import {
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
} from '@mui/icons-material'
import Logo from '@/components/Logo'
import type { FooterContent } from '@/data/types'

export interface FooterSectionProps {
  content: FooterContent
  // eslint-disable-next-line no-unused-vars
  onLinkClick?: (href: string) => void
  // eslint-disable-next-line no-unused-vars
  onSocialClick?: (platform: string, url: string) => void
  className?: string
  testId?: string
}

// Icon mapping for social platforms
const iconMap: Record<string, React.ReactNode> = {
  Facebook: <FacebookIcon />,
  Twitter: <TwitterIcon />,
  Instagram: <InstagramIcon />,
  LinkedIn: <LinkedInIcon />,
}

export const FooterSection: React.FC<FooterSectionProps> = ({
  content,
  onLinkClick,
  onSocialClick,
  className,
  testId = 'footer-section',
}) => {
  const handleLinkClick = (
    _event: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    if (onLinkClick) {
      onLinkClick(href)
    }
  }

  const handleSocialClick = (
    _event: React.MouseEvent<HTMLAnchorElement>,
    platform: string,
    url: string
  ) => {
    if (onSocialClick) {
      onSocialClick(platform, url)
    }
  }

  return (
    <Box
      component="footer"
      data-testid={testId}
      className={className}
      sx={{
        py: 6,
        backgroundColor: 'grey.100',
        color: 'grey.900',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Column 1: Logo and Tagline */}
          <Grid item xs={12} md={2.5} sx={{ textAlign: 'center' }}>
            <Box sx={{ mb: 2 }}>
              <Logo
                size={60}
                variant="dark"
                ariaLabel="NCESP Logo - National Council for Equal Shared Parenting"
              />
            </Box>
            <Typography
              variant="body2"
              sx={{ color: 'grey.600', fontStyle: 'italic' }}
            >
              Because children deserve a relationship with both parents.
            </Typography>
          </Grid>

          {/* Column 2: Quick Links */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography
              variant="h6"
              component="h3"
              gutterBottom
              sx={{ fontWeight: 600, mb: 2 }}
            >
              Quick Links
            </Typography>
            <Box
              component="nav"
              aria-label="Footer navigation"
              sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
            >
              {content.quickLinks.map(link => (
                <Link
                  key={`${link.label}-${link.href}`}
                  href={link.href}
                  onClick={e => handleLinkClick(e, link.href)}
                  sx={{
                    color: 'grey.700',
                    textDecoration: 'none',
                    '&:hover': {
                      color: 'primary.main',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Column 3: Issues */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography
              variant="h6"
              component="h3"
              gutterBottom
              sx={{ fontWeight: 600, mb: 2 }}
            >
              Issues
            </Typography>
            <Box
              component="nav"
              aria-label="Issues navigation"
              sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
            >
              {content.issueLinks.map(link => (
                <Link
                  key={`${link.label}-${link.href}`}
                  href={link.href}
                  onClick={e => handleLinkClick(e, link.href)}
                  sx={{
                    color: 'grey.700',
                    textDecoration: 'none',
                    '&:hover': {
                      color: 'primary.main',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Column 4: Social Media */}
          <Grid item xs={12} md={3}>
            <Typography
              variant="h6"
              component="h3"
              gutterBottom
              sx={{ fontWeight: 600, mb: 2 }}
            >
              Follow Us
            </Typography>
            <Link
              href={`mailto:${content.contactInfo.email}`}
              aria-label="Email NCESP"
              sx={{
                color: 'grey.700',
                textDecoration: 'none',
                display: 'inline-block',
                mb: 1.5,
                '&:hover': {
                  color: 'primary.main',
                  textDecoration: 'underline',
                },
              }}
            >
              {content.contactInfo.email}
            </Link>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {content.contactInfo.socialLinks.map(link => (
                <Box
                  key={link.platform}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <IconButton
                    component="a"
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={link.label}
                    onClick={e => handleSocialClick(e, link.platform, link.url)}
                    sx={{
                      color: 'grey.700',
                      '&:hover': {
                        color: 'primary.main',
                        transform: 'scale(1.1)',
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    {iconMap[link.icon]}
                  </IconButton>
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{
                      color: 'grey.700',
                      fontWeight: 500,
                    }}
                  >
                    {link.platform}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>

        {content.legalInfo && (
          <Box
            aria-label="Legal organization information"
            sx={{
              mt: 4,
              pt: 3,
              color: 'grey.600',
            }}
          >
            <Typography
              variant="caption"
              component="p"
              sx={{
                display: 'block',
                lineHeight: 1.7,
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              {content.legalInfo.taxStatus}. EIN: {content.legalInfo.ein}.
            </Typography>
            <Typography
              variant="caption"
              component="p"
              sx={{
                display: 'block',
                lineHeight: 1.7,
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              Legal address: {content.legalInfo.legalAddress}.
            </Typography>
          </Box>
        )}

        {/* Copyright and Legal Links */}
        <Box
          sx={{
            mt: content.legalInfo ? 2 : 4,
            pt: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 2, md: 0 },
          }}
        >
          <Typography variant="body2" sx={{ color: 'grey.600' }}>
            © {new Date().getFullYear()} {content.organizationName}. All rights
            reserved.
          </Typography>
          {content.legalLinks.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 3,
              }}
            >
              {content.legalLinks.map(link => (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={e => handleLinkClick(e, link.href)}
                  sx={{
                    color: 'grey.600',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': {
                      color: 'grey.900',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  )
}

export default FooterSection
